import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  Paper,
  Chip,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Settings as SettingsIcon,
  AccountBalance as BankIcon,
  CurrencyRupee as CurrencyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Build as MaintenanceIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { adminSettingsAPI } from '../../../services/api';
import { toast } from 'react-toastify';

const AdminSettingsManagement = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [appStatus, setAppStatus] = useState({ maintenance: false, message: '' });
  const [togglingMaintenance, setTogglingMaintenance] = useState(false);
  
  const [formData, setFormData] = useState({
    auto_deposit: false,
    min_recharge_amount: 10.00,
    max_recharge_amount: 50000.00,
    min_withdrawal_amount: 100.00,
    max_withdrawal_amount: 25000.00,
    min_bet_amount: 10.00,
    max_bet_amount: 5000.00,
  });

  useEffect(() => {
    fetchAdminSettings();
    fetchAppStatus();
  }, []);

  const fetchAppStatus = async () => {
    try {
      const response = await adminSettingsAPI.getAppStatus();
      if (response.data.success) {
        setAppStatus({
          maintenance: response.data.maintenance,
          message: response.data.message
        });
      }
    } catch (error) {
      console.error('Failed to fetch app status:', error);
    }
  };

  const fetchAdminSettings = async () => {
    try {
      setLoading(true);
      const response = await adminSettingsAPI.getAdminSettings();
      if (response.data.success) {
        const settingsData = response.data.settings;
        setSettings(settingsData);
        setFormData({
          auto_deposit: settingsData.auto_deposit || false,
          min_recharge_amount: parseFloat(settingsData.min_recharge_amount) || 10.00,
          max_recharge_amount: parseFloat(settingsData.max_recharge_amount) || 50000.00,
          min_withdrawal_amount: parseFloat(settingsData.min_withdrawal_amount) || 100.00,
          max_withdrawal_amount: parseFloat(settingsData.max_withdrawal_amount) || 25000.00,
          min_bet_amount: parseFloat(settingsData.min_bet_amount) || 10.00,
          max_bet_amount: parseFloat(settingsData.max_bet_amount) || 5000.00,
        });
      }
    } catch (error) {
      toast.error('Failed to fetch admin settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    const errors = [];

    if (formData.min_recharge_amount >= formData.max_recharge_amount) {
      errors.push('Minimum recharge amount must be less than maximum recharge amount');
    }

    if (formData.min_withdrawal_amount >= formData.max_withdrawal_amount) {
      errors.push('Minimum withdrawal amount must be less than maximum withdrawal amount');
    }

    if (formData.min_bet_amount >= formData.max_bet_amount) {
      errors.push('Minimum bet amount must be less than maximum bet amount');
    }

    if (formData.min_recharge_amount <= 0 || formData.max_recharge_amount <= 0) {
      errors.push('Recharge amounts must be greater than 0');
    }

    if (formData.min_withdrawal_amount <= 0 || formData.max_withdrawal_amount <= 0) {
      errors.push('Withdrawal amounts must be greater than 0');
    }

    if (formData.min_bet_amount <= 0 || formData.max_bet_amount <= 0) {
      errors.push('Bet amounts must be greater than 0');
    }

    return errors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      toast.error(validationErrors[0]);
      return;
    }

    try {
      setSaving(true);
      const response = await adminSettingsAPI.updateAdminSettings(formData);
      if (response.data.success) {
        toast.success('Settings updated successfully');
        setSettings(response.data.settings);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleMaintenance = async () => {
    try {
      setTogglingMaintenance(true);
      const newMaintenanceState = !appStatus.maintenance;
      
      const response = await adminSettingsAPI.toggleAppMaintenance({ 
        maintenance: newMaintenanceState 
      });
      
      if (response.data.success) {
        setAppStatus({
          maintenance: response.data.maintenance,
          message: response.data.message
        });
        toast.success(
          `App maintenance mode ${newMaintenanceState ? 'enabled' : 'disabled'} successfully`
        );
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Failed to toggle maintenance mode'
      );
    } finally {
      setTogglingMaintenance(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading settings...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="600">
          Admin Settings
        </Typography>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={saving}
          size="medium"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {/* App Maintenance Alert */}
      {appStatus.maintenance && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'warning.main',
            bgcolor: 'rgba(255, 152, 0, 0.1)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5
          }}
        >
          <WarningIcon color="warning" />
          <Box>
            <Typography variant="body2" fontWeight="600" color="warning.main">
              App is currently in Maintenance Mode
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Users cannot access the app. Disable maintenance mode to restore normal operation.
            </Typography>
          </Box>
        </Box>
      )}

      {/* App Maintenance Mode Card */}
      <Card 
        elevation={3} 
        sx={{ 
          mb: 3, 
          border: appStatus.maintenance ? '2px solid' : '1px solid',
          borderColor: appStatus.maintenance ? 'warning.main' : 'divider',
          bgcolor: appStatus.maintenance ? 'warning.lighter' : 'background.paper'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Box 
                sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: appStatus.maintenance ? 'warning.main' : 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <MaintenanceIcon fontSize="large" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="600">
                  App Maintenance Mode
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {appStatus.message}
                </Typography>
                <Chip 
                  label={appStatus.maintenance ? 'MAINTENANCE ON' : 'APP RUNNING'}
                  color={appStatus.maintenance ? 'warning' : 'success'}
                  size="small"
                  sx={{ mt: 1, fontWeight: 600 }}
                />
              </Box>
            </Box>
            
            <Box display="flex" alignItems="center" gap={2}>
              <Tooltip 
                title={appStatus.maintenance 
                  ? "Click to disable maintenance mode and allow users to access the app" 
                  : "Click to enable maintenance mode and prevent user access"}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" fontWeight="500">
                    {appStatus.maintenance ? 'Disable' : 'Enable'}
                  </Typography>
                  <Switch
                    checked={appStatus.maintenance}
                    onChange={handleToggleMaintenance}
                    disabled={togglingMaintenance}
                    color={appStatus.maintenance ? 'warning' : 'primary'}
                    sx={{
                      '& .MuiSwitch-thumb': {
                        bgcolor: appStatus.maintenance ? 'warning.main' : 'primary.main',
                      }
                    }}
                  />
                </Box>
              </Tooltip>
              
              {togglingMaintenance && (
                <Typography variant="caption" color="text.secondary">
                  Updating...
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {/* First Row - All Main Cards */}
        <Grid item xs={12} lg={3}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <SettingsIcon color="primary" fontSize="small" />
                <Typography variant="h6" fontWeight="500">General Configuration</Typography>
              </Box>
              
              <Box display="flex" alignItems="center" justifyContent="space-between" 
                   sx={{ p: 1.5, bgcolor: 'grey', borderRadius: 1, mb: 2 }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight="500">Auto Deposit</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Auto process deposits
                  </Typography>
                </Box>
                <Switch
                  checked={formData.auto_deposit}
                  onChange={(e) => handleInputChange('auto_deposit', e.target.checked)}
                  color="primary"
                />
              </Box>
              
              {/* Status Info */}
              {settings && (
                <Box sx={{ p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
                  <Typography variant="caption" color="info.dark" display="block">
                    Created by: {settings.creator?.username || 'System'}
                  </Typography>
                  <Typography variant="caption" color="info.dark" display="block">
                    Updated: {settings.updated_at ? new Date(settings.updated_at).toLocaleDateString() : 'Never'}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={3}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <TrendingUpIcon color="success" fontSize="small" />
                <Typography variant="h6" fontWeight="500" color="success.main">Deposit Limits</Typography>
              </Box>
              
              <Box display="flex" flexDirection="column" gap={1.5}>
                <TextField
                  label="Minimum Amount"
                  type="number"
                  size="small"
                  fullWidth
                  value={formData.min_recharge_amount}
                  onChange={(e) => handleInputChange('min_recharge_amount', parseFloat(e.target.value) || 0)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  inputProps={{ min: 1, step: 0.01 }}
                />
                
                <TextField
                  label="Maximum Amount"
                  type="number"
                  size="small"
                  fullWidth
                  value={formData.max_recharge_amount}
                  onChange={(e) => handleInputChange('max_recharge_amount', parseFloat(e.target.value) || 0)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  inputProps={{ min: 1, step: 0.01 }}
                />
                
                <Box sx={{ p: 1, bgcolor: 'success.light', borderRadius: 1, textAlign: 'center' }}>
                  <Typography variant="caption" fontWeight="500" color="success.dark">
                    {formatCurrency(formData.min_recharge_amount)} - {formatCurrency(formData.max_recharge_amount)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={3}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <TrendingDownIcon color="warning" fontSize="small" />
                <Typography variant="h6" fontWeight="500" color="warning.main">Withdrawal Limits</Typography>
              </Box>
              
              <Box display="flex" flexDirection="column" gap={1.5}>
                <TextField
                  label="Minimum Amount"
                  type="number"
                  size="small"
                  fullWidth
                  value={formData.min_withdrawal_amount}
                  onChange={(e) => handleInputChange('min_withdrawal_amount', parseFloat(e.target.value) || 0)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  inputProps={{ min: 1, step: 0.01 }}
                />
                
                <TextField
                  label="Maximum Amount"
                  type="number"
                  size="small"
                  fullWidth
                  value={formData.max_withdrawal_amount}
                  onChange={(e) => handleInputChange('max_withdrawal_amount', parseFloat(e.target.value) || 0)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  inputProps={{ min: 1, step: 0.01 }}
                />
                
                <Box sx={{ p: 1, bgcolor: 'warning.light', borderRadius: 1, textAlign: 'center' }}>
                  <Typography variant="caption" fontWeight="500" color="black">
                    {formatCurrency(formData.min_withdrawal_amount)} - {formatCurrency(formData.max_withdrawal_amount)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={3}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <CurrencyIcon color="primary" fontSize="small" />
                <Typography variant="h6" fontWeight="500" color="primary.main">Betting Limits</Typography>
              </Box>
              
              <Box display="flex" flexDirection="column" gap={1.5}>
                <TextField
                  label="Minimum Bet"
                  type="number"
                  size="small"
                  fullWidth
                  value={formData.min_bet_amount}
                  onChange={(e) => handleInputChange('min_bet_amount', parseFloat(e.target.value) || 0)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  inputProps={{ min: 1, step: 0.01 }}
                />
                
                <TextField
                  label="Maximum Bet"
                  type="number"
                  size="small"
                  fullWidth
                  value={formData.max_bet_amount}
                  onChange={(e) => handleInputChange('max_bet_amount', parseFloat(e.target.value) || 0)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  inputProps={{ min: 1, step: 0.01 }}
                />
                
                <Box sx={{ p: 1, bgcolor: 'primary.light', borderRadius: 1, textAlign: 'center' }}>
                  <Typography variant="caption" fontWeight="500" color="primary.dark">
                    {formatCurrency(formData.min_bet_amount)} - {formatCurrency(formData.max_bet_amount)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminSettingsManagement;